export const WEBP_IMAGE_QUALITY = 0.2;

const getWebPFileName = (fileName: string) => {
  const baseName = fileName.replace(/\.[^/.]+$/, "") || "image";
  return `${baseName}.webp`;
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected image could not be read."));
    };
    image.src = objectUrl;
  });

/**
 * Converts a browser-selected image to WebP before it is previewed or uploaded.
 * Non-image files are returned unchanged so mixed document/image uploaders remain safe.
 */
export const convertImageToWebP = async (
  file: File,
  quality = WEBP_IMAGE_QUALITY,
): Promise<File> => {
  if (!file.type.startsWith("image/")) return file;

  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Image conversion is not supported in this browser.");
  }

  context.drawImage(image, 0, 0);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (!result || result.type !== "image/webp") {
          reject(new Error("WebP conversion is not supported in this browser."));
          return;
        }
        resolve(result);
      },
      "image/webp",
      quality,
    );
  });

  return new File([blob], getWebPFileName(file.name), {
    type: "image/webp",
    lastModified: file.lastModified,
  });
};
